import { Button, Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import useAuthForms from '../../../../hooks/useAuthForms';
import { useLoginForm } from '../../../../hooks/useLoginForm';
import { LOGIN_MODE, type LoginMode } from '../../../../types/enums';
import NeobetAuthFormShell from '../auth/NeobetAuthFormShell';
import { NeobetPasswordField, NeobetTextField } from '../molecules/NeobetFormFields';

type NeobetBaseLoginFormProps = {
  mode?: LoginMode;
  testId: string;
};

const styles = {
  forgotPasswordRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    mt: -0.5,
  },
  forgotPasswordButton: (theme: Theme) => ({
    minWidth: 'auto',
    p: 0,
    color: theme.palette.primary.main,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '20px',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: 'transparent',
      color: alpha(theme.palette.primary.main, 0.78),
    },
  }),
};

export const NeobetBaseLoginForm = ({ mode = LOGIN_MODE.EMAIL, testId }: NeobetBaseLoginFormProps) => {
  const { openRegisterForm, openForgotPasswordForm } = useAuthForms();
  const { t, control, errors, isSubmitting, handleClose, onSubmit } = useLoginForm({ mode });

  const isUsernameMode = mode === LOGIN_MODE.USERNAME;

  return (
    <Box data-testid={testId}>
      <NeobetAuthFormShell
        title={t('login.title')}
        submitTitle={t('login.title')}
        onSubmit={onSubmit}
        onClose={handleClose}
        isSubmitting={isSubmitting}
        footerText={t('register.noAccountYet')}
        footerActionLabel={t('register.register')}
        onFooterAction={() => openRegisterForm()}
        footerTestId={`${testId}-footer`}
        footerActionTestId={`${testId}-open-register`}
        testId={testId}
      >
        <NeobetTextField
          name={isUsernameMode ? 'username' : 'email'}
          control={control}
          label={t(isUsernameMode ? 'register.username' : 'register.email')}
          variant='outlined'
          fullWidth
          type='text'
          error={!!errors[isUsernameMode ? 'username' : 'email']}
          helperText={errors[isUsernameMode ? 'username' : 'email']?.message as string}
          disabled={isSubmitting}
          autoFocus
        />
        <NeobetPasswordField
          name='password'
          control={control}
          label={t('register.password')}
          error={!!errors.password}
          helperText={errors.password?.message as string}
          disabled={isSubmitting}
          slotProps={{ htmlInput: { autoComplete: 'new-password' } }}
        />
        <Box sx={styles.forgotPasswordRow}>
          <Button
            type='button'
            variant='text'
            onClick={openForgotPasswordForm}
            data-testid={`${testId}-forgot-password`}
            sx={styles.forgotPasswordButton}
          >
            {t('forgotPassword.title')}?
          </Button>
        </Box>
      </NeobetAuthFormShell>
    </Box>
  );
};

const NeobetLoginForm = () => {
  return <NeobetBaseLoginForm testId='neobet-login-form' />;
};

export default NeobetLoginForm;
